import { getPathPageName } from './getPathPageName';
import { analytics } from './segment';
import { buildFeedInteractionPayload } from './types/FeedInteractionFields';
import { resolveInteractionTab } from './utils/resolveInteractionTab';

/**
 * One entry of a view event's `items`, serialized from the DOM.
 */
export type AutoItem = {
  /** The child's raw `data-testid`, unparsed. */
  testid?: string;
  /** Scrubbed, truncated visible label. */
  label?: string;
  /** 0-indexed position among the serialized children. */
  index: number;
};

/**
 * Context present on every auto event.
 *
 * `ancestorTestids` is doing the work that `section`, `entity_type`, `entity_id`
 * and `surface_chain` used to: it is the raw containment chain, from which
 * Data Analytics can derive hierarchy, entity and placement themselves — and
 * revise that derivation later without a deploy.
 */
type AutoSharedParams = {
  /** `data-testid` of each ancestor, nearest first. Capped. */
  ancestorTestids?: string[];
  /** Search session id — a runtime value with no DOM presence, so declared. */
  searchId?: string;
};

/** A click. The subject is the element that was clicked. */
type AutoClickParams = AutoSharedParams & {
  actionType: 'clicked';
  /** Raw `data-testid`, unparsed. Absent when there is none, or it failed the safety filter. */
  elementTestid?: string;
  /** Lowercase tag name, e.g. `button`. */
  elementTag: string;
  /** ARIA role, when set. */
  elementRole?: string;
  /** Scrubbed, truncated visible label. Descriptive only. */
  elementLabel?: string;
  /** Raw `href`, when the element has one. */
  elementHref?: string;
  /** `aria-expanded` as it was *before* the click, when the element has it. */
  ariaExpandedBefore?: string;
};

/** A container impression. The subject is the container itself. */
type AutoViewParams = AutoSharedParams & {
  actionType: 'viewed';
  /** Raw `data-testid` of the observed container. */
  elementTestid?: string;
  /** What was on show when the container was seen. May be capped — see below. */
  items?: AutoItem[];
  /**
   * True number of items present, which can exceed `items.length` when the
   * serialized array was capped. Never derive this from `items.length`: a
   * truncated array would silently under-report the count.
   */
  itemsTotal?: number;
  /** True when `items` was capped and does not list everything present. */
  itemsTruncated?: boolean;
};

type ComponentInteractionAutoParams = AutoClickParams | AutoViewParams;

/**
 * Emits `UX Component Interaction Auto` — the autocaptured (Layer 1) event.
 *
 * **This payload carries facts, never interpretations.** Every field is a
 * reversible read of the DOM at the moment of interaction: what the testid was,
 * what tag and role the element had, what it linked to, what its ancestors were.
 * Nothing here decides what any of that *means*.
 *
 * That rule is deliberate and it is a reversal of an earlier design, which
 * emitted `entity_type` (a word-scan mapping `recipe`→`pairing`),
 * `interaction_pattern` (partly an English regex over `aria-label`),
 * `element_index`/`element_total` (a peer group found by walking up the DOM until
 * the count exceeded one) and a testid split into template plus instance. All of
 * those were guesses encoded as columns: a wrapper `div` added during a refactor
 * silently changed them, and once a guess is in the warehouse it cannot be
 * revised without a deploy and a backfill.
 *
 * Interpretation belongs to Data Analytics, working from these facts. Where a
 * specific event genuinely needs something the DOM does not contain — a price, a
 * query result, an availability flag — that is a Layer 2 or Layer 3 addition, not
 * a heuristic here.
 *
 * The two exceptions are safety, not interpretation, and they stay: labels are
 * scrubbed of PII-shaped text, and a testid that looks like interpolated free
 * text is dropped rather than emitted.
 *
 * Deliberately a **separate event name** from `UX Component Interaction`, so
 * every existing consumer of the curated event keeps working untouched rather
 * than silently absorbing a high-volume, lower-resolution stream.
 */
export const componentInteractionAuto = (
  params: ComponentInteractionAutoParams
) => {
  const shared = {
    action_type: params.actionType,
    capture_method: 'auto',

    element_testid: params.elementTestid,
    ancestor_testids: params.ancestorTestids,
    search_id: params.searchId,

    // Ambient — resolved centrally, never passed by a call site.
    page_name: getPathPageName(),
    client: 'web',
    ...buildFeedInteractionPayload({
      tab: resolveInteractionTab(undefined),
    }),
  };

  if (params.actionType === 'viewed') {
    analytics.track('UX Component Interaction Auto', {
      ...shared,
      items: params.items,
      // Top-level count so "# viewed / # searches" needs no array unnesting.
      // This is the TRUE count, not the length of a possibly-capped array.
      items_total: params.itemsTotal,
      items_truncated: params.itemsTruncated,
    });
    return;
  }

  analytics.track('UX Component Interaction Auto', {
    ...shared,
    element_tag: params.elementTag,
    element_role: params.elementRole,
    element_label: params.elementLabel,
    element_href: params.elementHref,
    aria_expanded_before: params.ariaExpandedBefore,
  });
};
